import { type FC, type ForwardedRef, Fragment } from "react";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { TextLink } from "@root/shared/components/TextLink";
import { Label } from "@root/shared/components/Label";
import { WorkStatusLabel } from "@userFrontend/features/publication/components/WorkStatusLabel";
import { Salaries } from "@userFrontend/features/publication/components/Salaries";
import { TelInformation } from "@userFrontend/features/publication/components/TelInformation";
import { AwardsLabel } from "@userFrontend/components/AwardsLabel";
import { Icon } from "@root/shared/components/Icon";
import { ShopWorksModal } from "@userFrontend/features/publication/components/ShopWorksModal";
import { ClientReviewLabel } from "./uiParts/ClientReviewLabel";
import { CassetteTable } from "./uiParts/CassetteTable";
import { ShowShopWorksButton } from "./uiParts/ShowShopWorksButton";
import { ShopWorkCassette } from "./uiParts/ShopWorkCassette";
import { Buttons } from "./uiParts/Buttons";
import { type WorkWithShopWorkCassetteProps, type ModalLinkParams } from "./type";
import {
  GA_MODAL_SHOP_WORK_ENTRY,
  GA_MODAL_SHOP_WORK_LINK,
  GA_MODAL_SHOP_WORK_SCOUT_ENTRY,
  GA_MODAL_SHOP_WORK_SCOUT_LINK,
  GA_SHOP_WORK_LINK,
  GA_SHOW_MODAL_BUTTON,
  GA_WORK_ENTRY,
  GA_WORK_KEEP,
  GA_WORK_LINK,
  GA_WORK_SCOUT,
} from "./constants";
import * as styles from "./styles.css";

type Props = {
  isShowModal: boolean;
  handleShowModal: () => void;
  handleCloseModal: () => void;
  handleClickLink: (params: ModalLinkParams) => void;
  handleClickModalLink: (params: ModalLinkParams) => void;
  indicatorRef: ForwardedRef<HTMLDivElement>;
  showShopWorksButtonRef: ForwardedRef<HTMLButtonElement>;
  defaultShowShopNumber: number;
  hiddenShopNumber: number;
  gaLabel: string;
  isShowIndicator: boolean;
} & Omit<WorkWithShopWorkCassetteProps, "secondaryContentAsync">;

export const Presentation: FC<Props> = ({
  statusLabelFlags: { newContentFlag, publishingEndUpCloseFlag, premiumScoutFlag, scoutFlag },
  imgSrc,
  shopName,
  brandName,
  businessTypeAndOccupation,
  detailUrl,
  title,
  isRejobAgentClient,
  awardsLabel,
  isReviewLabel,
  featureLabels,
  employmentAndSalaryType,
  clientShops: { shopInformations, totalNumber },
  isLogin,
  keepButtonUrl,
  hasKept,
  enterableFlag,
  entriedFlag,
  scoutedFlag,
  scoutedEntryUrl,
  scoutDeadline,
  workCode,
  query,
  entryUrl,
  gaLabel,
  telNumberContent: { telNumber, isShowTellButton },
  shopWorks,
  isShowModal,
  handleShowModal,
  handleCloseModal,
  handleClickLink,
  handleClickModalLink,
  defaultShowShopNumber,
  hiddenShopNumber,
  isShowIndicator,
  indicatorRef,
  showShopWorksButtonRef,
  shopAddress,
}) => (
  <div className={styles.cassette}>
    <div
      onClick={(e) =>
        handleClickLink({
          url: detailUrl,
          query,
          event: e,
          gaAction: GA_WORK_LINK,
          gaLabel,
        })
      }
      onKeyDown={(e) =>
        handleClickLink({
          url: detailUrl,
          query,
          event: e,
          gaAction: GA_WORK_LINK,
          gaLabel,
        })
      }
      role="button"
      tabIndex={0}
      data-job-code={workCode}
      id={`code-${workCode}`}
      className={styles.content}
    >
      <div className={styles.headerOuter}>
        <div className={styles.statusLabelsOuter}>
          {premiumScoutFlag && <WorkStatusLabel status="premiumScout" />}
          {scoutFlag && <WorkStatusLabel status="scout" />}
          {newContentFlag && <WorkStatusLabel status="newContent" />}
          {publishingEndUpCloseFlag && <WorkStatusLabel status="publishingEndUpClose" />}
        </div>
        <div className={styles.spImageOuter}>
          <LazyLoadImage src={imgSrc} alt="" width={657} height={370} />
        </div>
        <div className={styles.brandNameOuter}>
          {isRejobAgentClient && (
            <p className={styles.agentClientLabelSp}>リジョブエージェント求人</p>
          )}
          {brandName}｜{businessTypeAndOccupation}
        </div>
      </div>
      <div className={styles.titleAndFeatureLabelsOuter}>
        {isRejobAgentClient && (
          <p className={styles.agentClientLabelPc}>リジョブエージェント求人</p>
        )}
        {scoutedFlag && !shopWorks?.length && (
          <p className={styles.scoutedByShop}>この店舗からのスカウト</p>
        )}
        <p className={styles.shopNameOuter}>
          <span className={styles.shopLocationIcon}>
            <Icon name="location" />
          </span>
          {shopName}
        </p>
        <p className={styles.textLinkOuter}>
          <a
            href={detailUrl}
            className={styles.textLink}
            onClick={(e) =>
              handleClickLink({
                url: detailUrl,
                query,
                event: e,
                gaAction: GA_WORK_LINK,
                gaLabel,
              })
            }
            data-ga-action={GA_WORK_LINK}
            data-ga-label={gaLabel}
            data-query-str={query}
          >
            {title}
          </a>
        </p>
        <div className={styles.featureLabelsOuter}>
          {awardsLabel && (
            <span className={styles.awardsLabelOuter}>
              <AwardsLabel rank={awardsLabel.rank}>{awardsLabel.children}</AwardsLabel>
            </span>
          )}
          {isReviewLabel && <ClientReviewLabel />}
          {featureLabels.length > 0 && (
            <>
              {featureLabels
                .filter((label) => label.selected)
                .map(({ name }) => (
                  <Label theme="deepPink" key={name}>
                    {name}
                  </Label>
                ))}
              {featureLabels
                .filter((label) => !label.selected)
                .map(({ name }) => (
                  <Label theme="lightPink" key={name}>
                    {name}
                  </Label>
                ))}
            </>
          )}
        </div>
      </div>
      <div className={styles.tableAndImageOuter}>
        <div className={styles.salariesAndShopInformationsOuter}>
          <div className={styles.salariesOuter}>
            <CassetteTable title="給与" iconName="yen">
              <div className={styles.salariesContainer}>
                <Salaries employmentAndSalaryType={employmentAndSalaryType} />
              </div>
            </CassetteTable>
          </div>
          <div className={styles.shopInformationsOuter}>
            <CassetteTable title="店舗" iconName="location">
              <div style={{ width: "100%" }}>
                <div className={styles.addressOuter}>
                  <p className={styles.oneLineEllipsis}>
                    {shopAddress.prefecture} {shopAddress.city} {shopAddress.detailAddress}
                  </p>
                  <p className={styles.oneLineEllipsis}>{shopAddress.station}</p>
                </div>
                {shopInformations.length > 0 && (
                  <div
                    className={styles.shopInformationsContainer}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="button"
                    tabIndex={0}
                  >
                    <p className={styles.shopInformationsTitle}>他の店舗でも募集しています</p>
                    {shopInformations.map((shopInformation, index) => {
                      const shopId = shopInformation.docId.match(/.*_(.*)$/);
                      return (
                        <Fragment key={shopInformation.detailUrl}>
                          {index < defaultShowShopNumber && (
                            <div
                              key={shopInformation.detailUrl}
                              className={styles.shopInformationOuter}
                            >
                              <p className={styles.otherShopNameOuter}>
                                <TextLink
                                  isBold
                                  href={shopInformation.detailUrl}
                                  onClick={(e) =>
                                    handleClickLink({
                                      url: shopInformation.detailUrl,
                                      query,
                                      event: e,
                                      gaAction: GA_SHOP_WORK_LINK,
                                      gaLabel: `${workCode}_${shopId ? shopId[0] : ""}`,
                                    })
                                  }
                                  data-ga-action={GA_SHOP_WORK_LINK}
                                  data-ga-label={`${workCode}_${shopId ? shopId[0] : ""}`}
                                >
                                  {shopInformation.shopName}
                                </TextLink>
                              </p>
                              <p className={styles.oneLineEllipsis}>
                                {shopInformation.prefecture} {shopInformation.city}{" "}
                                {shopInformation.detailAddress}
                              </p>
                              {shopInformation.detailNearestStation.length > 0 && (
                                <p className={styles.oneLineEllipsis}>
                                  {shopInformation.detailNearestStation}
                                </p>
                              )}
                            </div>
                          )}
                        </Fragment>
                      );
                    })}
                    {hiddenShopNumber > 0 && shopWorks && shopWorks.length > 2 && (
                      <div className={styles.showShopWorksButtonOuter}>
                        <ShowShopWorksButton
                          shopLength={hiddenShopNumber}
                          onClick={handleShowModal}
                          data-ga-action={GA_SHOW_MODAL_BUTTON}
                          data-ga-label={gaLabel}
                          ref={showShopWorksButtonRef}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CassetteTable>
          </div>
        </div>
        <div className={styles.pcImageOuter}>
          <LazyLoadImage src={imgSrc} alt="" width={657} height={370} />
        </div>
      </div>
    </div>
    <div className={styles.footerOuter}>
      <div className={styles.buttonsOuter}>
        <Buttons
          login={isLogin}
          keepUrl={keepButtonUrl}
          keepGaAction={GA_WORK_KEEP}
          keepGaLabel={gaLabel}
          hasKept={hasKept}
          handleClickLink={handleClickLink}
          size="large"
          enterableFlag={enterableFlag}
          entriedFlag={entriedFlag}
          scoutedFlag={scoutedFlag}
          scoutedEntryUrl={scoutedEntryUrl}
          scoutDeadline={scoutDeadline}
          workCode={workCode}
          scoutGaAction={GA_WORK_SCOUT}
          scoutGaLabel={gaLabel}
          query={query}
          entryUrl={entryUrl}
          entryGaAction={GA_WORK_ENTRY}
          entryGaLabel={gaLabel}
        />
      </div>
      {isShowTellButton && (
        <TelInformation workCode={workCode} telNumberContent={{ telNumber, isShowTellButton }} />
      )}
    </div>
    {shopWorks && shopWorks.length > 2 && (
      <div className={styles.modalOuter}>
        <ShopWorksModal
          isShow={isShowModal}
          brandName={brandName}
          businessTypeAndOccupation={businessTypeAndOccupation}
          totalNumber={totalNumber}
          handleCloseModal={handleCloseModal}
        >
          <div>
            {shopWorks.map((shopWork) => {
              const matchedClientShopId = shopWork.docId.match(/.*_(.*)$/);
              const shopWorkGaLabel = `${workCode}_${matchedClientShopId ? matchedClientShopId[1] : "null"}`;

              return (
                <div key={shopWork.docId} className={styles.shopCassetteOuter}>
                  <ShopWorkCassette
                    {...shopWork}
                    handleClickLink={handleClickModalLink}
                    linkGaAction={
                      shopWork.scoutedFlag ? GA_MODAL_SHOP_WORK_SCOUT_LINK : GA_MODAL_SHOP_WORK_LINK
                    }
                    scoutGaAction={GA_MODAL_SHOP_WORK_SCOUT_ENTRY}
                    entryGaAction={GA_MODAL_SHOP_WORK_ENTRY}
                    gaLabel={shopWorkGaLabel}
                  />
                </div>
              );
            })}
            {isShowIndicator && (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} ref={indicatorRef} />
              </div>
            )}
          </div>
        </ShopWorksModal>
      </div>
    )}
  </div>
);
